<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Subscriber Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.subscriber_name')"
                v-model.trim="filterOptions.subscriber_name"
              />
              <!-- End:: Subscriber Name Input -->

              <!-- Start:: Mobile Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.subscriber_mobile')"
                v-model.trim="filterOptions.subscriber_mobile"
              />
              <!-- End:: Mobile Input -->

              <!-- Start:: Package Name Input -->
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.package_name')"
                v-model.trim="filterOptions.package_name"
              />
              <!-- End:: Package Name Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="3"
                :optionsList="subscriptionStatuses"
                :placeholder="$t('PLACEHOLDERS.subscription_status')"
                v-model="filterOptions.status"
              />
              <!-- End:: Status Input -->

              <!-- Start:: From Date -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.from_date')"
                v-model.trim="filterOptions.from"
              />
              <!-- End:: From Date -->

              <!-- Start:: To Date -->
              <base-picker-input
                col="3"
                type="date"
                :placeholder="$t('PLACEHOLDERS.to_date')"
                v-model.trim="filterOptions.to"
              />
              <!-- End:: To Date -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("PLACEHOLDERS.subscriptions") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.id`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>

        <!-- Start:: Subscriber Name -->
        <template v-slot:[`item.subscriber_name`]="{ item }">
          <p>{{ item.subscriber?.user?.name || "-" }}</p>
        </template>
        <!-- End:: Subscriber Name -->

        <!-- Start:: Subscription Date -->
        <template v-slot:[`item.created_at`]="{ item }">
          <p>{{ item.created_at || "-" }}</p>
        </template>
        <!-- End:: Subscription Date -->

        <!-- Start:: Package Name -->
        <template v-slot:[`item.package_name`]="{ item }">
          <p>{{ item.package?.name || "-" }}</p>
        </template>
        <!-- End:: Package Name -->

        <!-- Start:: Price -->
        <template v-slot:[`item.price`]="{ item }">
          <p>{{ item.price || "-" }} {{ $t("CURRENCY.sr") }}</p>
        </template>
        <!-- End:: Price -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status`]="{ item }">
          <span
            :class="{
              'text-success': item.status === 'active',
              'text-danger':
                item.status === 'expired' || item.status === 'cancelled',
            }"
          >
            {{ item._status || item.status }}
          </span>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip
              placement="bottom"
              v-if="$can('subscriptions show', 'subscriptions')"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button
                class="btn_show"
                @click="$router.push(`/subscriptions/show/${item.id}`)"
              >
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllSubscriptions",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        subscriber_name: null,
        subscriber_mobile: null,
        package_name: null,
        status: null,
        from: null,
        to: null,
      },
      // End:: Filter Data

      // Start:: Subscription Statuses
      subscriptionStatuses: [
        {
          id: "active",
          name: this.$t ? this.$t("STATUS.active") : "نشط",
          nameAr: "نشط",
          nameEn: "Active",
        },
        {
          id: "expired",
          name: this.$t ? this.$t("STATUS.expired") : "منتهي",
          nameAr: "منتهي",
          nameEn: "Expired",
        },
        // {
        //   id: "cancelled",
        //   name: this.$t ? this.$t("STATUS.cancelled") : "ملغي",
        //   nameAr: "ملغي",
        //   nameEn: "Cancelled",
        // },
      ],
      // End:: Subscription Statuses

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.ImagesSpaces.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.subscriber_name"),
          value: "subscriber_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_date"),
          value: "created_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.package_name"),
          value: "package_name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.paid_amount"),
          value: "price",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.subscription_status"),
          value: "status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handle Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/subscriptions/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.subscriber_name = null;
      this.filterOptions.subscriber_mobile = null;
      this.filterOptions.package_name = null;
      this.filterOptions.status = null;
      this.filterOptions.from = null;
      this.filterOptions.to = null;

      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/subscriptions/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handle Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Subscriptions
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },

    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "subscriptions",
          params: {
            page: this.paginations.current_page,
            subscriber_name: this.filterOptions.subscriber_name,
            subscriber_mobile: this.filterOptions.subscriber_mobile,
            package_name: this.filterOptions.package_name,
            status: this.filterOptions.status?.id,
            from: this.filterOptions.from,
            to: this.filterOptions.to,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.data;
        this.paginations.last_page = res.data.data.meta.last_page;
        this.paginations.items_per_page = res.data.data.meta.per_page;
      } catch (error) {
        this.loading = false;
         this.$message.error(error.response.data.message);
        console.log(error.response.data.message);
      }
    },
    // End:: Set Table Rows
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

/* .btn_show {
  background: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  margin: 0 2px;
}

.btn_show:hover {
  background: #0056b3; */
/* } */

.actions {
  display: flex;
  justify-content: center;
  gap: 8px;
}
</style>
